import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types mirror employee OrdersSlice but scoped for backoffice
export interface PaymentInformation {
  _id: string;
  bp_payment_status: boolean;
  bp_payment_amount_due: number;
  bp_payment_amount: number;
  bp_payment_amount_refunded: number;
  bp_order_information_id: string;
  bp_payment_created_at: string;
  bp_payment_updated_at: string;
  __v: number;
  bp_payment_already_paid_amount?: number;
  bp_payment_method?: string;
  bp_payments?: Array<{ amount: number; method?: string; date?: string }>;
  bp_payment_amount_received?: number;
  /** Location the order was placed at (the order doc itself carries none). */
  bp_payment_location_id?: string;
}

export interface ProductInformation {
  _id: string;
  bp_owner_id?: string;
  bp_product_name: string;
  bp_product_category?: string;
  bp_product_sub_category?: string;
  bp_product_unit_of_mass?: string;
  bp_product_mass?: number | string | null;
  bp_product_buying_price?: number | null;
  bp_product_selling_price: number | null;
  bp_product_quantity?: number | null;
  bp_product_size?: string;
  bp_product_model?: string;
  bp_product_price_type?: 'item' | 'unit' | string;
  /** Missing on lines saved before services existed: a product. */
  bp_product_kind?: 'product' | 'service';
  bp_product_image?: string;
  bp_variant_id?: string;
  bp_variant_attributes?: Record<string, string>;
  bp_product_has_variants?: boolean;
  bp_product_variant_attributes?: string[];
  bp_product_parent_name?: string;
}

export interface SalesProductInformation {
  bp_order_completed?: boolean;
  bp_order_created_at?: string;
  /** Line total (selling price × qty/mass). Omitted by the owner endpoints. */
  bp_order_price?: number | null;
  bp_order_quantity?: number | null;
  bp_order_mass?: number | null;
  bp_order_updated_at?: string;
  bp_product_information: ProductInformation;
  bp_sales_id?: string;
  bp_order_paid_mass?: number | null;
  bp_order_paid_quantity?: number | null;
}

export interface AssignedStaffInformation {
  _id: string;
  bp_employee_name: string;
  bp_employee_role: string;
}

export interface OrderDoc {
  bp_order_customer_information: string;
  /** The linked back-office customer and their phone, from the server. */
  bp_order_customer_id?: string | null;
  bp_order_customer_phone_number?: string | null;
  bp_order_assigned_staff_information?: AssignedStaffInformation | null;
  bp_order_id: string;
  bp_order_master_created_at: string;
  bp_order_master_order_status: string; // completed | pending | partial
  bp_order_master_receipt_reference_id: string;
  bp_order_master_payment_information: PaymentInformation;
  bp_order_master_updated_at: string;
  bp_sales_products_information: SalesProductInformation[];
  bp_total_sales_products: number;
}

export interface PaginatedOrders {
  docs: OrderDoc[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number;
  page: number;
  perPage: number;
  totalDocs: number;
  totalPages: number;
}

export type OrdersTab = 'Completed' | 'Pending' | 'Partial';

export interface BackofficeOrdersState {
  tab: OrdersTab;
  loading: boolean;
  error?: string | null;
  completed: PaginatedOrders;
  pending: PaginatedOrders;
  partial: PaginatedOrders;
  // Search state (receipt ID search)
  tableSearchTerm: string;
  tableSearch: PaginatedOrders;
}

const createInitialPage = (): PaginatedOrders => ({
  docs: [],
  hasNextPage: true,
  hasPrevPage: true,
  limit: 100,
  nextPage: 1,
  page: 0,
  perPage: 100,
  totalDocs: 0,
  totalPages: 0,
});

const initialState: BackofficeOrdersState = {
  tab: 'Completed',
  loading: false,
  error: null,
  completed: createInitialPage(),
  pending: createInitialPage(),
  partial: createInitialPage(),
  tableSearchTerm: '',
  tableSearch: createInitialPage(),
};

const mergeOrders = (
  existing: OrderDoc[],
  incoming: OrderDoc[],
): OrderDoc[] => {
  const orderMap = new Map<string, OrderDoc>();
  [...existing, ...incoming].forEach(order => {
    const key = order.bp_order_id;
    const prev = orderMap.get(key);
    if (
      !prev ||
      new Date(order.bp_order_master_updated_at).getTime() >
        new Date(prev.bp_order_master_updated_at).getTime()
    ) {
      orderMap.set(key, { ...order });
    }
  });
  return Array.from(orderMap.values()).sort(
    (a, b) =>
      new Date(b.bp_order_master_updated_at).getTime() -
      new Date(a.bp_order_master_updated_at).getTime(),
  );
};

const ordersSlice = createSlice({
  name: 'backofficeOrders',
  initialState,
  reducers: {
    setTab: (state, action: PayloadAction<OrdersTab>) => {
      state.tab = action.payload;
    },
    setOrdersPerPage: (
      state,
      action: PayloadAction<{ tab: OrdersTab; perPage: number }>,
    ) => {
      const size = Math.max(1, Number(action.payload.perPage || 20));
      const key =
        action.payload.tab === 'Completed'
          ? 'completed'
          : action.payload.tab === 'Pending'
            ? 'pending'
            : 'partial';
      state[key].perPage = size;
      state[key].limit = size;
    },
    setOrdersLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setOrdersError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addCompletedOrders: (state, action: PayloadAction<PaginatedOrders>) => {
      const merged = mergeOrders(state.completed.docs, action.payload.docs);
      state.completed = { ...state.completed, ...action.payload, docs: merged };
    },
    addPendingOrders: (state, action: PayloadAction<PaginatedOrders>) => {
      const merged = mergeOrders(state.pending.docs, action.payload.docs);
      state.pending = { ...state.pending, ...action.payload, docs: merged };
    },
    addPartialOrders: (state, action: PayloadAction<PaginatedOrders>) => {
      const merged = mergeOrders(state.partial.docs, action.payload.docs);
      state.partial = { ...state.partial, ...action.payload, docs: merged };
    },
    clearCompleted: state => {
      state.completed = createInitialPage();
    },
    clearPending: state => {
      state.pending = createInitialPage();
    },
    clearPartial: state => {
      state.partial = createInitialPage();
    },
    // Search reducers
    setTableSearchTerm: (state, action: PayloadAction<string>) => {
      state.tableSearchTerm = action.payload;
    },
    setTableSearchResults: (state, action: PayloadAction<PaginatedOrders>) => {
      const merged = mergeOrders(state.tableSearch.docs, action.payload.docs);
      state.tableSearch = {
        ...state.tableSearch,
        ...action.payload,
        docs: merged,
      };
    },
    setTableSearchPagination: (
      state,
      action: PayloadAction<{ page?: number; perPage?: number }>,
    ) => {
      if (typeof action.payload.page === 'number') {
        state.tableSearch.page = Math.max(1, action.payload.page);
      }
      if (typeof action.payload.perPage === 'number') {
        const size = Math.max(1, Number(action.payload.perPage || 20));
        state.tableSearch.perPage = size;
        state.tableSearch.limit = size;
      }
    },
    clearTableSearch: state => {
      state.tableSearch = createInitialPage();
      state.tableSearchTerm = '';
    },
    removeOrderById: (state, action: PayloadAction<string>) => {
      const orderId = String(action.payload || '').trim();
      if (!orderId) return;

      const removeById = (arr: OrderDoc[]) =>
        arr.filter(o => o.bp_order_id !== orderId);
      state.completed.docs = removeById(state.completed.docs);
      state.pending.docs = removeById(state.pending.docs);
      state.partial.docs = removeById(state.partial.docs);
    },
    upsertOrderDoc: (state, action: PayloadAction<OrderDoc>) => {
      const updated = action.payload;
      const statusLower = String(
        updated.bp_order_master_order_status || '',
      ).toLowerCase();

      // Remove the order from all tabs first
      const removeById = (arr: OrderDoc[]) =>
        arr.filter(o => o.bp_order_id !== updated.bp_order_id);
      state.completed.docs = removeById(state.completed.docs);
      state.pending.docs = removeById(state.pending.docs);
      state.partial.docs = removeById(state.partial.docs);

      // Decide target tab based on status
      const targetKey: 'completed' | 'pending' | 'partial' =
        statusLower.includes('partial')
          ? 'partial'
          : statusLower === 'completed'
            ? 'completed'
            : 'pending';

      // Merge in updated doc and keep list sorted/unique
      state[targetKey].docs = mergeOrders(state[targetKey].docs, [updated]);
    },
  },
});

export const {
  setTab,
  setOrdersPerPage,
  setOrdersLoading,
  setOrdersError,
  addCompletedOrders,
  addPendingOrders,
  addPartialOrders,
  clearCompleted,
  clearPending,
  clearPartial,
  setTableSearchTerm,
  setTableSearchResults,
  setTableSearchPagination,
  clearTableSearch,
  removeOrderById,
  upsertOrderDoc,
} = ordersSlice.actions;

export default ordersSlice.reducer;
